import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Target,
  FileText,
  Users,
  Compass,
  Database,
  GraduationCap,
  ArrowRight,
  CheckCircle,
  Building2,
  Factory,
  Hotel,
  ShoppingBag,
  Heart,
  Home,
  Leaf,
  TrendingUp,
  Shield,
  BarChart3,
  Lightbulb,
  Settings,
  BookOpen,
  Presentation,
  Globe,
  Award,
} from 'lucide-react';

const servicesData = {
  'materiality-assessment': {
    icon: Target,
    title: 'Materiality Assessment',
    subtitle: 'Focus on What Drives Risk, Return, and Reputation.',
    description: 'Materiality is the foundation of credible ESG strategy. Without it, organisations overcommit, misallocate resources, and dilute impact. SusTek delivers structured, defensible materiality assessments aligned with global standards (GRI, ISSB, SASB, TCFD) and tailored to your industry landscape.',
    whatWeDeliver: [
      'Double materiality analysis (impact + financial risk/opportunity)',
      'Stakeholder mapping and engagement frameworks',
      'Risk-opportunity prioritisation matrices',
      'Industry benchmarking',
      'Board-ready visual dashboards',
      'Audit-ready documentation trail',
    ],
    businessValue: [
      'Align ESG investments with enterprise risk priorities',
      'Improve investor and lender confidence',
      'Strengthen governance credibility',
      'Avoid reputational blind spots',
      'Establish a defensible foundation for reporting',
    ],
    highlight: 'Materiality is not a workshop exercise. It is a strategic filter for capital allocation and long-term resilience.',
    ctaMid: 'Unsure which ESG issues truly matter to your business?',
    ctaMidAction: 'Book a Materiality Scoping Consultation',
    ctaEnd: 'Ready to establish a defensible ESG foundation?',
    ctaEndAction: 'Schedule a Materiality Strategy Session Today',
  },
  'esg-reporting': {
    icon: FileText,
    title: 'ESG Reporting & Compliance Disclosure',
    subtitle: 'Compliance-Ready. Investor-Aligned. Industry-Specific.',
    description: 'Regulatory expectations are intensifying across jurisdictions — from ISSB and CSRD to SGX climate disclosures. SusTek delivers ESG reports that integrate regulatory compliance with operational realities and sector-specific metrics.',
    industries: [
      { icon: Factory, name: 'Manufacturing & Electronics', focus: 'Energy intensity, scope 3 supply chain mapping, waste circularity' },
      { icon: Hotel, name: 'Hospitality & Tourism', focus: 'Carbon per room, water footprint optimisation, guest sustainability engagement' },
      { icon: ShoppingBag, name: 'Retail & F&B', focus: 'Traceability, food waste management, sustainable sourcing' },
      { icon: Heart, name: 'Healthcare & Care Operations', focus: 'Facility efficiency, clinical waste management' },
      { icon: Home, name: 'Real Estate & Asset Owners', focus: 'Asset-level ESG performance, green financing readiness' },
    ],
    whatWeDeliver: [
      'ESG report drafting (GRI, ISSB, SASB, TCFD)',
      'Climate risk and scenario disclosures',
      'Scope 1, 2, 3 carbon inventories',
      'Data traceability documentation',
      'Investor-ready narrative development',
      'Regulatory submission support',
    ],
    businessValue: [
      'Reduce regulatory exposure',
      'Strengthen ESG ratings',
      'Improve capital access',
      'Support sustainability-linked loans',
      'Enhance brand credibility',
    ],
    highlight: 'An ESG report should support your valuation story — not just your compliance checklist.',
    ctaMid: 'Preparing for your next reporting cycle?',
    ctaMidAction: 'Request an ESG Reporting Readiness Assessment',
    ctaEnd: "Let's build a report that withstands investor due diligence.",
    ctaEndAction: 'Engage Our ESG Reporting Team',
  },
  'esg-outsourcing': {
    icon: Users,
    title: 'ESG Services Outsourcing',
    subtitle: 'Your Embedded ESG Function — Without Expanding Headcount.',
    description: 'Many organisations face capability gaps, resource constraints, or fragmented ESG responsibilities. SusTek operates as your extended ESG office, providing structured governance, continuity, and reporting discipline.',
    whatWeDeliver: [
      'ESG Officer-as-a-Service',
      'KPI monitoring and tracking',
      'Reporting cycle management',
      'Supplier ESG coordination',
      'Regulatory updates monitoring',
      'Board-level dashboard reporting',
      'Documentation and audit support',
    ],
    engagementModels: [
      'Monthly retainer',
      'Multi-site coordination',
      'Project-based cycles',
      'Hybrid embedded advisory',
    ],
    businessValue: [
      'Reduce internal hiring burden',
      'Maintain regulatory continuity',
      'Improve data consistency',
      'Accelerate ESG maturity',
      'Scale across regions or business units',
    ],
    highlight: 'You gain institutional ESG capability — without building a department from scratch.',
    ctaMid: 'Need ESG expertise but not a full-time team?',
    ctaMidAction: 'Explore ESG Outsourcing Options',
    ctaEnd: 'Strengthen governance without increasing overhead.',
    ctaEndAction: 'Speak to Our ESG Advisory Lead',
  },
  'strategy-program-design': {
    icon: Compass,
    title: 'Strategy and Program Designs',
    subtitle: 'Turn ESG Ambition into Operational Execution.',
    description: 'Vision statements do not reduce emissions. Programs do. SusTek designs executable ESG strategies aligned to financial performance, operational feasibility, and digital enablement.',
    whatWeDesign: [
      'Decarbonisation and net-zero roadmaps',
      'Energy and water optimisation programs',
      'Circular economy transitions',
      'Sustainable procurement frameworks',
      'ESG-linked innovation initiatives',
      'Community and social impact integration',
      'Digital sustainability transformation plans',
    ],
    ourApproach: [
      'Financial modelling',
      'Risk analysis',
      'Technology enablement',
      'Governance structures',
      'KPI frameworks',
      'Change management roadmaps',
    ],
    businessValue: [
      'Reduce operating costs',
      'Unlock green funding and incentives',
      'Enhance resilience',
      'Improve supply chain stability',
      'Strengthen long-term enterprise value',
    ],
    highlight: 'Strategy must be measurable, budgeted, and governable — otherwise it remains aspirational.',
    ctaMid: 'Looking to design a credible net-zero or ESG roadmap?',
    ctaMidAction: 'Request a Strategy Blueprint Workshop',
    ctaEnd: "Let's turn your sustainability vision into structured execution.",
    ctaEndAction: 'Start Your ESG Transformation Planning',
  },
  'data-curation': {
    icon: Database,
    title: 'Data Curation and Management',
    subtitle: 'Build Finance-Grade ESG Data Infrastructure.',
    description: 'Sustainability is increasingly data-driven. Investors and regulators now expect traceable, auditable, and comparable ESG metrics. SusTek designs and implements ESG data systems that eliminate spreadsheet dependency and support real-time insight.',
    whatWeDeliver: [
      'ESG data architecture design',
      'Carbon accounting systems',
      'Scope 1, 2, 3 modelling frameworks',
      'Data validation and QA processes',
      'Visual dashboards',
      'Digital twin integration',
      'API-ready ESG data pipelines',
      'Audit trail documentation',
    ],
    advancedCapabilities: [
      'IoT integration',
      'Supply chain data consolidation',
      'Finance-grade MRV preparation',
      'Carbon asset readiness and tokenisation enablement',
    ],
    businessValue: [
      'Reduce manual reporting inefficiencies',
      'Increase audit readiness',
      'Improve decision-making speed',
      'Strengthen financing eligibility',
      'Reduce reporting risk',
    ],
    highlight: 'Reliable ESG data is now part of corporate infrastructure — not just sustainability reporting.',
    ctaMid: 'Struggling with fragmented ESG data?',
    ctaMidAction: 'Book an ESG Data Architecture Review',
    ctaEnd: 'Build data systems that support finance-grade sustainability.',
    ctaEndAction: 'Start Your Data Transformation',
  },
  'training-enablement': {
    icon: GraduationCap,
    title: 'Training & Enablement',
    subtitle: 'Build Capability. Activate Strategy. Scale Impact.',
    description: 'Sustainability transformation does not succeed through reports alone. It succeeds when leaders, managers, and operational teams understand how sustainability connects to risk, performance, and profitability. SusTek designs and delivers structured sustainability curriculum, executive workshops, and industry-specific seminars across the region.',
    whatWeOffer: {
      curriculum: [
        'Industry-aligned sustainability modules',
        'ESG governance and compliance training',
        'Double materiality and risk workshops',
        'Net-zero transition fundamentals',
        'Finance-grade MRV and reporting readiness',
        'Sustainable innovation and product design',
      ],
      workshops: [
        'Design thinking labs for sustainable transformation',
        'Board-level ESG strategy sessions',
        'Cross-functional ESG alignment workshops',
        'Data-driven sustainability decision labs',
        'Scenario-based climate risk simulations',
      ],
      delivery: [
        'In-person workshops',
        'Executive roundtables',
        'Hybrid seminars',
        'Multi-country programs',
        'Sector-focused masterclasses',
      ],
    },
    caseStudies: [
      {
        client: 'Microsoft Singapore',
        title: 'Sustainable Choices: A Sustainable Design Thinking Lab for Marketers',
        impact: 'Strengthened sustainability storytelling credibility and enhanced risk awareness in brand messaging',
      },
      {
        client: 'IBPAP (IT & Business Process Association of the Philippines)',
        title: 'Double Materiality Assessment Workshop',
        impact: 'Improved industry-wide understanding of emerging ESG compliance and board-level reporting preparedness',
      },
      {
        client: 'ePM Singapore / Malaysia',
        title: 'Project Management for Sustainability',
        impact: 'Enhanced integration of ESG into project decision-making and cross-border sustainability project governance',
      },
      {
        client: 'Singapore SME Centre @ SMCCI',
        title: 'Sustainability as a Competitive Advantage in Hospitality',
        impact: 'Increased awareness of sustainability as revenue driver and identified quick-win operational efficiencies',
      },
    ],
    whoIsFor: [
      'Corporate leadership teams',
      'Marketing and brand leaders',
      'Project managers',
      'ESG and sustainability officers',
      'SME operators',
      'Industry associations',
      'Government-linked entities',
    ],
    businessValue: [
      'Accelerate ESG literacy across leadership and teams',
      'Reduce implementation resistance',
      'Improve reporting readiness',
      'Strengthen governance maturity',
      'Identify new innovation and revenue opportunities',
      'Align sustainability with profitability',
    ],
    highlight: 'Training is not an event. It is an organisational multiplier.',
    ctaMid: 'Looking to upskill your leadership or teams in sustainability strategy?',
    ctaMidAction: 'Request a Customised Training Proposal',
    ctaEnd: 'Empower your organisation with structured sustainability capability.',
    ctaEndAction: 'Schedule a Sustainability Training Consultation',
  },
};

const allServices = [
  { key: 'materiality-assessment', ...servicesData['materiality-assessment'] },
  { key: 'esg-reporting', ...servicesData['esg-reporting'] },
  { key: 'esg-outsourcing', ...servicesData['esg-outsourcing'] },
  { key: 'strategy-program-design', ...servicesData['strategy-program-design'] },
  { key: 'data-curation', ...servicesData['data-curation'] },
  { key: 'training-enablement', ...servicesData['training-enablement'] },
];

// Services Landing Page Component
const ServicesLanding = () => {
  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="outline" className="mb-6 border-teal/30 text-teal bg-teal/10">
              Sustainability & ESG Services
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Build ESG Systems That{' '}
              <span className="text-teal">Withstand Scrutiny</span> — and Create Value
            </h1>
            <p className="text-xl text-white/70 mb-8 max-w-3xl mx-auto">
              Sustainability today influences capital access, regulatory standing, operational efficiency, 
              and market differentiation. We help organisations move from reactive compliance to structured, 
              finance-grade sustainability systems.
            </p>
            <Link to="/book-call">
              <Button variant="teal" size="xl" className="group">
                Book a Consultation
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Integrated ESG Services
            </h2>
            <p className="text-lg text-muted-foreground">
              Our integrated ESG services combine strategy, governance, and data engineering — ensuring 
              sustainability becomes embedded in business performance, not isolated in reporting.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allServices.map((service) => (
              <Link key={service.key} to={`/services/${service.key}`}>
                <Card className="h-full hover-lift border-border/50 bg-card group cursor-pointer">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-xl bg-teal/10 text-teal flex items-center justify-center mb-4 group-hover:bg-teal group-hover:text-white transition-colors">
                      <service.icon className="h-7 w-7" />
                    </div>
                    <CardTitle className="text-xl group-hover:text-teal transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {service.subtitle}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {service.description}
                    </p>
                    <div className="mt-4 flex items-center text-teal text-sm font-medium group-hover:gap-2 transition-all">
                      Learn more
                      <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready to Transform Your ESG Approach?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Let's build sustainability systems that are measurable, defensible, and scalable.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book-call">
              <Button variant="teal" size="xl" className="group">
                Book a Discovery Call
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="xl">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Individual Service Page Component
const ServiceDetail = ({ type }) => {
  const service = servicesData[type];

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  if (!service) {
    return <ServicesLanding />;
  }

  const Icon = service.icon;

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <Link to="/services" className="inline-flex items-center text-white/60 hover:text-white mb-6 transition-colors">
              <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
              Back to Services
            </Link>
            <div className="flex items-start gap-6 mb-6">
              <div className="w-16 h-16 rounded-xl bg-teal/20 text-teal flex items-center justify-center flex-shrink-0">
                <Icon className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                  {service.title}
                </h1>
                <p className="text-xl text-teal font-medium">
                  {service.subtitle}
                </p>
              </div>
            </div>
            <p className="text-lg text-white/70 max-w-3xl">
              {service.description}
            </p>
          </div>
        </div>
      </section>

      {/* Industries Section (for ESG Reporting) */}
      {service.industries && (
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-foreground mb-8">Industry-Specific Expertise</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {service.industries.map((industry, index) => (
                <Card key={index} className="border-border/50">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-teal/10 text-teal flex items-center justify-center flex-shrink-0">
                        <industry.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{industry.name}</h3>
                        <p className="text-sm text-muted-foreground">{industry.focus}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What We Deliver Section */}
      <section className={`py-16 ${service.industries ? 'bg-muted/30' : 'bg-background'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-6">
                {service.whatWeDesign ? 'What We Design' : service.whatWeOffer ? 'What We Offer' : 'What We Deliver'}
              </h2>
              <ul className="space-y-3">
                {(service.whatWeDeliver || service.whatWeDesign || []).map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Training Specific Content */}
              {service.whatWeOffer && (
                <div className="space-y-8 mt-8">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-teal" />
                      Custom Sustainability Curriculum
                    </h3>
                    <ul className="space-y-2">
                      {service.whatWeOffer.curriculum.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle className="h-4 w-4 text-teal flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Presentation className="h-5 w-5 text-teal" />
                      Interactive Workshops & Labs
                    </h3>
                    <ul className="space-y-2">
                      {service.whatWeOffer.workshops.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle className="h-4 w-4 text-teal flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Globe className="h-5 w-5 text-teal" />
                      Regional Delivery
                    </h3>
                    <ul className="space-y-2">
                      {service.whatWeOffer.delivery.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle className="h-4 w-4 text-teal flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Engagement Models for Outsourcing */}
              {service.engagementModels && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Flexible Engagement Models</h3>
                  <div className="flex flex-wrap gap-2">
                    {service.engagementModels.map((model, index) => (
                      <Badge key={index} variant="secondary" className="bg-teal/10 text-teal">
                        {model}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Our Approach for Strategy */}
              {service.ourApproach && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Our Approach</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {service.ourApproach.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                        <Settings className="h-4 w-4 text-teal" />
                        <span className="text-sm text-foreground">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Advanced Capabilities for Data */}
              {service.advancedCapabilities && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Advanced Capabilities</h3>
                  <ul className="space-y-2">
                    {service.advancedCapabilities.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Lightbulb className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div>
              <h2 className="text-2xl font-bold text-foreground mb-6">Business Value</h2>
              <ul className="space-y-3 mb-8">
                {service.businessValue.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <TrendingUp className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Highlight Quote */}
              <Card className="border-teal/20 bg-teal/5">
                <CardContent className="pt-6">
                  <blockquote className="text-lg font-medium text-foreground italic">
                    "{service.highlight}"
                  </blockquote>
                </CardContent>
              </Card>

              {/* Who Is For (Training) */}
              {service.whoIsFor && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Who This Is For</h3>
                  <div className="flex flex-wrap gap-2">
                    {service.whoIsFor.map((item, index) => (
                      <Badge key={index} variant="outline" className="bg-background">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Mid CTA */}
              <Card className="mt-8 border-border/50 bg-muted/30">
                <CardContent className="pt-6">
                  <p className="text-foreground font-medium mb-4">{service.ctaMid}</p>
                  <Link to="/book-call">
                    <Button variant="teal" className="group">
                      {service.ctaMidAction}
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section (Training) */}
      {service.caseStudies && (
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-foreground mb-8 flex items-center gap-3">
              <Award className="h-6 w-6 text-teal" />
              Past Engagements & Case Studies
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {service.caseStudies.map((study, index) => (
                <Card key={index} className="border-border/50 hover-lift">
                  <CardHeader>
                    <Badge variant="secondary" className="w-fit mb-2 bg-teal/10 text-teal">
                      {study.client}
                    </Badge>
                    <CardTitle className="text-lg">{study.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Impact:</strong> {study.impact}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {service.ctaEnd}
          </h2>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/book-call">
              <Button variant="teal" size="xl" className="group">
                {service.ctaEndAction}
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="xl">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8">Explore Other Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {allServices
              .filter((s) => s.key !== type)
              .slice(0, 3)
              .map((s) => (
                <Link key={s.key} to={`/services/${s.key}`}>
                  <Card className="h-full hover-lift border-border/50 group cursor-pointer">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-teal/10 text-teal flex items-center justify-center flex-shrink-0 group-hover:bg-teal group-hover:text-white transition-colors">
                          <s.icon className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground group-hover:text-teal transition-colors">
                            {s.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mt-1">{s.subtitle}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const Services = ({ type }) => {
  if (!type) {
    return <ServicesLanding />;
  }
  return <ServiceDetail type={type} />;
};

export default Services;
